"use client";

import type React from "react";
import { useState } from "react";
import { RotatingLines } from "react-loader-spinner";
import { FiAlertCircle } from "react-icons/fi";
import type {
  CreateProjectModalProps,
  CreateProjectPayload,
} from "../../../../Interfaces/FeatureFlip";
import styles from "./CreateProjectModal.module.css";
import { useCreateProject } from "API/Queries/useCreateProject";
import { emptyStringsToNull } from "Utils/utils";
import RequiredStar from "Components/Common/RequiredStar";

const CreateProjectModal = (props: CreateProjectModalProps) => {
  const { isOpen, onClose, onSubmit } = props;

  const [formData, setFormData] = useState<CreateProjectPayload>({
    projectName: "",
    projectDescription: "",
    contextName: "",
    contextDescription: "",
  });

  const [error, setError] = useState<string>();
  const { mutate: createProject, isLoading } = useCreateProject();

  // Extract complex logic
  const isFormValid = !!formData.projectName;
  const isSubmitDisabled = isLoading || !isFormValid;

  // Extract event handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateProject = (projectData: CreateProjectPayload) => {
    const payload = emptyStringsToNull(projectData) as CreateProjectPayload;
    setError(undefined);

    createProject(payload, {
      onSuccess: () => {
        onClose();
        onSubmit();
        setFormData({
          projectName: "",
          projectDescription: "",
          contextName: "",
          contextDescription: "",
        });
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create project";
        setError(errorMessage);
      },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCreateProject(formData);
  };

  const handleCloseClick = () => {
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.content}>
        <h2 className={styles.title}>Create New Project</h2>
        <div className={styles.formContent}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Project Name <RequiredStar />
              </label>
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                className={styles.input}
                placeholder="Enter project name"
                required
                disabled={isLoading}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Project Description</label>
              <textarea
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleChange}
                className={styles.textarea}
                placeholder="Enter project description"
                disabled={isLoading}
              />
            </div>
            {error && (
              <p className={styles.error}>
                <FiAlertCircle />
                {error}
              </p>
            )}
            <div className={styles.actions}>
              <button
                type="button"
                onClick={handleCloseClick}
                className={styles.cancelButton}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitDisabled}
              >
                {isLoading ? (
                  <RotatingLines
                    strokeColor="white"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="20"
                    visible={true}
                  />
                ) : (
                  "Create Project"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
